import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  CardGrid,
  DateField,
  Page,
  PageHeader,
  Select,
  StatCard,
  StatCardList,
  type IconName,
} from '@evogard/ui';
import { Chart } from '@evogard/ui/chart';
import {
  FINANCE_DASHBOARD_DATE_KINDS,
  FINANCE_DASHBOARD_BASIS_LABELS,
  businessDate,
  count,
  format as formatMoney,
  formatDate,
  loadFailureMessage,
  subtract,
  type FinanceDashboardBasis,
  type Money,
} from '@evogard/domain';
import type { FinanceDashboardWire } from '@evogard/contract';
import { api, ok } from '../../../lib/api.js';
import { useSessionContext } from '../../../lib/session.js';
import { FinanceStatesMap } from './FinanceStatesMap.js';
import { BalanceRanking } from './BalanceRanking.js';
import { compact, money } from './money.js';
import { RankingBars } from './RankingBars.js';
import { ShareChart } from './ShareChart.js';
import { TrendTile } from './TrendTile.js';

/**
 * O painel do Financeiro (29/10/2026): a receita e a despesa lançadas no
 * Financeiro, por cidade e estado, consultor e time, tipo e plano do veículo.
 *
 * As respostas do diretor que decidem cada corte estão no cabeçalho de
 * `packages/domain/src/finance-dashboard.ts`, e o rodapé da tela repete as
 * que mudam a leitura de um número.
 *
 * Três camadas, de cima para baixo, da pergunta mais ampla para a mais fina:
 * os totais com a tendência e a variação; os doze meses e o mapa; e os
 * cortes, cada um na forma que a direção lê de primeira — tipo em rosca,
 * plano em pizza, cidade e consultor em ranking, time em ranking do saldo
 * (29/10/2026). A rosca, a pizza e o ranking têm a chave Receitas / Despesas.
 */
export function FinanceDashboardScreen() {
  const [kind, setKind] = useState<'mes' | 'intervalo'>('mes');
  const [month, setMonth] = useState(businessDate(new Date()).slice(0, 7));
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [basis, setBasis] = useState<FinanceDashboardBasis>('competencia');
  const [state, setState] = useState<string | null>(null);

  const period = kind === 'mes' ? monthRange(month) : { from, to };
  const incomplete = !period.from || !period.to;
  const inverted = !incomplete && period.from > period.to;

  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const query = { from: period.from, to: period.to, basis };
  const dashboard = useQuery({
    queryKey: ['finance', 'dashboard', tenantId, query],
    queryFn: async () =>
      ok(
        await api.financeDashboard.dashboard({ query }),
        200,
        loadFailureMessage(undefined, 'o painel do Financeiro'),
      ),
    enabled: tenantId !== undefined && !incomplete && !inverted,
    placeholderData: (previous) => previous,
  });
  const d = dashboard.data;
  const loading = dashboard.isLoading;

  const revenue = d ? money(d.revenue.amount) : undefined;
  const expense = d ? money(d.expense.amount) : undefined;
  const beforeRevenue = d ? money(d.previous.revenue) : undefined;
  const beforeExpense = d ? money(d.previous.expense) : undefined;
  const beforeLabel = d ? periodLabel(d.previous.from, d.previous.to) : '';
  const trend = d?.trend.map((t) => {
    const r = money(t.revenue);
    const e = money(t.expense);
    return { month: t.month, revenue: r, expense: e, balance: subtract(r, e) };
  });

  return (
    <Page measure="cheia">
      <PageHeader title="Painel do Financeiro" />

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-44">
          <Select
            label="Período"
            value={kind}
            onChange={(e) => setKind(e.target.value as 'mes' | 'intervalo')}
          >
            <option value="mes">Por mês</option>
            <option value="intervalo">Intervalo</option>
          </Select>
        </div>
        {kind === 'mes' ? (
          <div className="w-52">
            <Select label="Mês" value={month} onChange={(e) => setMonth(e.target.value)}>
              {recentMonths().map((m) => (
                <option key={m} value={m}>
                  {monthLabel(m)}
                </option>
              ))}
            </Select>
          </div>
        ) : (
          <>
            <div className="w-44">
              <DateField label="De" value={from} onChange={setFrom} />
            </div>
            <div className="w-44">
              <DateField label="Até" value={to} onChange={setTo} />
            </div>
          </>
        )}
        <div className="w-52">
          <Select
            label="Data do lançamento"
            value={basis}
            onChange={(e) => setBasis(e.target.value as FinanceDashboardBasis)}
          >
            {FINANCE_DASHBOARD_DATE_KINDS.map((b) => (
              <option key={b} value={b}>
                {FINANCE_DASHBOARD_BASIS_LABELS[b]}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {inverted && <Alert tone="erro">O início do período vem depois do fim.</Alert>}
      {dashboard.error && <Alert tone="erro">{dashboard.error.message}</Alert>}
      {basis === 'pagamento' && (
        <Alert tone="informacao">
          Pela data do pagamento, só entra o que já foi pago ou recebido no período.
        </Alert>
      )}

      {/* os cinco cabem numa linha a partir do xl; o mínimo de 15rem os quebraria em 4 + 1 */}
      <StatCardList
        stretch
        className="xl:[grid-template-columns:repeat(5,minmax(0,1fr))]"
      >
        <TrendTile
          icon="seta-baixo"
          label="Receitas"
          legend="fase-azul"
          value={revenue === undefined ? undefined : formatMoney(revenue)}
          support={
            d
              ? `${count(d.revenue.count, 'lançamento', 'lançamentos')} · ${formatMoney(money(d.revenue.settled))} recebido`
              : undefined
          }
          series={trend?.map((t) => t.revenue)}
          now={revenue}
          before={beforeRevenue}
          beforeLabel={beforeLabel}
          higherIsBetter
        />
        <TrendTile
          icon="seta-cima"
          label="Despesas"
          legend="fase-laranja"
          value={expense === undefined ? undefined : formatMoney(expense)}
          support={
            d
              ? `${count(d.expense.count, 'lançamento', 'lançamentos')} · ${formatMoney(money(d.expense.settled))} pago`
              : undefined
          }
          series={trend?.map((t) => t.expense)}
          now={expense}
          before={beforeExpense}
          beforeLabel={beforeLabel}
          higherIsBetter={false}
        />
        <TrendTile
          icon="dinheiro"
          label="Saldo"
          value={
            revenue === undefined || expense === undefined
              ? undefined
              : formatMoney(subtract(revenue, expense))
          }
          support="receitas menos despesas"
          series={trend?.map((t) => t.balance)}
          now={
            revenue === undefined || expense === undefined
              ? undefined
              : subtract(revenue, expense)
          }
          before={
            beforeRevenue === undefined || beforeExpense === undefined
              ? undefined
              : subtract(beforeRevenue, beforeExpense)
          }
          beforeLabel={beforeLabel}
          higherIsBetter
        />
        <OpenCard
          icon="atencao"
          label="Vencidos"
          tone="erro"
          open={d?.overdue}
          expenseWord="vencida"
        />
        <OpenCard
          icon="relogio"
          label="Aguardando pagamento"
          tone="alerta"
          open={d?.awaiting}
          expenseWord="a pagar"
        />
      </StatCardList>

      <Chart
        kind="linha"
        area
        title="Os últimos 12 meses"
        descricao={`Receita e despesa de cada mês pela data de ${FINANCE_DASHBOARD_BASIS_LABELS[basis].toLowerCase()}.`}
        label="Receita e despesa nos últimos 12 meses"
        data={trend ?? []}
        loading={loading}
        error={dashboard.error}
        empty="Nenhum lançamento nos últimos 12 meses."
        category={(t) => shortMonth(t.month)}
        format={(v) => formatMoney(v as Money)}
        axisFormat={(v) => compact(v as Money).replace('R$ ', '')}
        series={[
          {
            key: 'receita',
            label: 'Receitas',
            color: 'fase-azul',
            value: (t) => t.revenue,
          },
          {
            key: 'despesa',
            label: 'Despesas',
            color: 'fase-laranja',
            value: (t) => t.expense,
          },
        ]}
      />

      <FinanceStatesMap
        states={d?.byState}
        cities={d?.byCity}
        loading={loading}
        selected={state}
        onSelect={setState}
      />

      <CardGrid>
        <ShareChart
          title="Por tipo de veículo"
          descricao="O tipo do veículo do lançamento."
          rows={d?.byVehicleType}
          loading={loading}
          error={dashboard.error}
          donut
        />
        <ShareChart
          title="Por plano"
          descricao="A categoria do veículo no SGA."
          rows={d?.byVehiclePlan}
          loading={loading}
          error={dashboard.error}
        />
      </CardGrid>

      <CardGrid>
        <RankingBars
          title="Por cidade"
          descricao="Pelo endereço do associado no cadastro."
          first="Cidade"
          rows={d?.byCity}
          loading={loading}
        />
        <RankingBars
          title="Por consultor"
          descricao="O voluntário que está no veículo hoje."
          first="Consultor"
          rows={d?.byConsultant}
          loading={loading}
        />
      </CardGrid>

      <BalanceRanking
        title="Por time"
        descricao="O time do consultor no Comercial, do que dá mais resultado para o que dá menos."
        first="Time"
        rows={d?.byTeam}
        loading={loading}
      />

      <p className="text-xs text-texto-suave">
        Entram as receitas e as despesas lançadas no Financeiro — a mensalidade do SGA
        fica de fora. A despesa recusada, a receita cancelada e a estornada não contam. O
        consultor é o voluntário que está no veículo hoje: uma transferência de carteira
        muda os meses passados. O pagamento de vários veículos não é dividido entre as
        placas e aparece em "Vários veículos".
      </p>
    </Page>
  );
}

/**
 * Um dos cartões do que está em aberto: o número grande é a receita (o que a
 * casa tem a receber) e o rodapé, a despesa (o que ela tem a pagar). A cor
 * de estado só acende quando há algo em aberto — zero não é má notícia.
 */
function OpenCard({
  icon,
  label,
  tone,
  open,
  expenseWord,
}: {
  icon: IconName;
  label: string;
  tone: 'erro' | 'alerta';
  open: FinanceDashboardWire['overdue'] | undefined;
  expenseWord: string;
}) {
  const revenue = open ? money(open.revenue.amount) : undefined;
  const expense = open ? money(open.expense.amount) : undefined;
  return (
    <StatCard
      icon={icon}
      label={label}
      tone={revenue || expense ? tone : 'neutro'}
      value={revenue === undefined ? undefined : formatMoney(revenue)}
      support={
        open
          ? `a receber · ${count(open.revenue.count, 'receita', 'receitas')}`
          : undefined
      }
      footer={
        open && expense !== undefined ? (
          <span>
            <span className="font-semibold text-texto tabular-nums">
              {formatMoney(expense)}
            </span>{' '}
            de despesa {expenseWord} ·{' '}
            {count(open.expense.count, 'lançamento', 'lançamentos')}
          </span>
        ) : undefined
      }
    />
  );
}

/** `AAAA-MM` → do dia 1 ao último dia do mês. */
function monthRange(month: string): { from: string; to: string } {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
}

/** Os doze meses até o corrente, do mais novo para o mais velho. */
function recentMonths(): string[] {
  const [y, m] = businessDate(new Date()).split('-').map(Number) as [number, number];
  return Array.from({ length: 12 }, (_, i) =>
    new Date(Date.UTC(y, m - 1 - i, 1)).toISOString().slice(0, 7),
  );
}

function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1, 15)).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** "out/26" — o rótulo do eixo da tendência. */
function shortMonth(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const name = new Date(Date.UTC(y, m - 1, 15)).toLocaleDateString('pt-BR', {
    month: 'short',
    timeZone: 'UTC',
  });
  return `${name.replace('.', '')}/${String(y).slice(2)}`;
}

/** "set/26" quando o anterior é um mês inteiro; senão as duas datas, curtas — cabe no rodapé do cartão. */
function periodLabel(from: string, to: string): string {
  const range = monthRange(from.slice(0, 7));
  if (range.from === from && range.to === to) return shortMonth(from.slice(0, 7));
  if (from.slice(0, 7) === to.slice(0, 7) || to === monthRange(to.slice(0, 7)).to) {
    return `${formatDate(from).slice(0, 5)}–${formatDate(to).slice(0, 5)}`;
  }
  return `${formatDate(from)}–${formatDate(to)}`;
}
